import React, {FC, ReactNode, useState} from 'react';
import styled, {css} from 'styled-components';

import {ClickEventHandler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon, IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisuallyHidden} from '../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserTheme, useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

interface WindowsTitleBarButtonProps {
  variant?: WindowsTitleBarButtonVariantsEnum;
  label: ReactNode;
  iconName: IconName;
  onClick: ClickEventHandler;
}

export enum WindowsTitleBarButtonVariantsEnum {
  DEFAULT = 'DEFAULT',
  DANGER = 'DANGER',
}

/*
 * Style.
 */

interface ButtonStyleProps {
  $variant: WindowsTitleBarButtonVariantsEnum;
  $hoverState: HoverStatesEnum;
}

enum HoverStatesEnum {
  DID_LEAVE = 'DID_LEAVE',
  DID_LEAVE_AFTER_CLICK = 'DID_LEAVE_AFTER_CLICK',
  DID_ENTER = 'DID_ENTER',
}

const hoverBackgroundColors = {
  [WindowsTitleBarButtonVariantsEnum.DEFAULT]: (theme: UserTheme) => theme.alphas.white20,
  // This color does not exist in our palette.
  [WindowsTitleBarButtonVariantsEnum.DANGER]: () => '#e81123',
};

const hoverStyles = {
  [HoverStatesEnum.DID_LEAVE]: css`
    transition: background-color 0.3s;
  `,
  [HoverStatesEnum.DID_LEAVE_AFTER_CLICK]: css`
    transition: none;
  `,
  [HoverStatesEnum.DID_ENTER]: css<ButtonStyleProps>`
    background-color: ${(p) => hoverBackgroundColors[p.$variant](p.theme)};
    transition: none;
  `,
};

const StyledButton = styled(ChromelessButton)<ButtonStyleProps>`
  width: 44px;
  margin-left: 1px;

  ${center()};

  ${(p) => hoverStyles[p.$hoverState]};
`;

/*
 * Component.
 */

export const WindowsTitleBarButton: FC<WindowsTitleBarButtonProps> = ({
  variant = WindowsTitleBarButtonVariantsEnum.DEFAULT,
  label,
  iconName,
  onClick,
}) => {
  const theme = useUserTheme();
  // Track our own hover state to work around Electron quirks.
  const [hoverState, setHoverState] = useState(HoverStatesEnum.DID_LEAVE);

  return (
    <StyledButton
      $variant={variant}
      $hoverState={hoverState}
      onClick={onClick}
      onMouseEnter={() => setHoverState(HoverStatesEnum.DID_ENTER)}
      onMouseLeave={() => setHoverState(HoverStatesEnum.DID_LEAVE)}
      // Prevent the hover state from sticking after minimizing, closing, etc.
      onMouseUp={() => setHoverState(HoverStatesEnum.DID_LEAVE_AFTER_CLICK)}
    >
      <Icon name={iconName} color={theme.greys.white} />
      <VisuallyHidden>{label}</VisuallyHidden>
    </StyledButton>
  );
};
